import React, { useState, useEffect } from 'react';
import { Card, Collapse, Input, Button, Space, Alert, Spin, message, Popconfirm, Tag } from 'antd';
import { SaveOutlined, ReloadOutlined, UndoOutlined } from '@ant-design/icons';
import { adminAPI } from '../api/admin';

const { TextArea } = Input;

// ── برچسب فارسی هر کلید — فقط برای خوانایی؛ هر کلید جدیدی که در آینده به
// site_messages اضافه شود، حتی بدون برچسب اینجا، خودکار (با نام خام کلید) نمایش داده می‌شود.
const LABELS = {
  registration_closed: 'ثبت‌نام بسته است',
  registration_closed_waitlist: 'ثبت‌نام بسته است (با راهنمایی به لیست انتظار)',
  invite_required: 'کد دعوت الزامی است',
  invite_invalid: 'کد دعوت نامعتبر است',
  invite_expired: 'کد دعوت منقضی شده است',
  invite_used: 'کد دعوت قبلاً استفاده شده است',
  invite_wrong_phone: 'کد دعوت متعلق به شماره دیگری است',
  waitlist_disabled: 'لیست انتظار غیرفعال است',

  platform_pass: 'موفقیت در عبور از سکو',
  platform_pass_with_ticket: 'موفقیت در عبور از سکو + بلیت قطار',
  platform_fail_waitlist_auto_invite: 'شکست در سکو — ورودی از لیست انتظار',
  platform_fail_public_registration: 'شکست در سکو — ثبت‌نام عمومی',
  platform_fail_friend_referral: 'شکست در سکو — دعوت دوست (رفرال)',
  platform_fail_generic: 'شکست در سکو — پیام عمومی',
  platform_attempts_exhausted: 'اتمام تعداد تلاش‌های مجاز سکو',
  initial_galleons_message: 'پیام دریافت گالیون هدیه هنگام ثبت‌نام',

  waitlist_confirmation_sms: 'پیامک تأیید ثبت در لیست انتظار',
  platform_success_sms: 'پیامک موفقیت عبور از سکو',
  invitation_gift_sms: 'پیامک دعوت‌نامه‌ی هدیه',

  train_missed_notice: 'پیام «قطارت را از دست دادی» (صفحه پیام محرمانه)',
  train_reminder_3d: 'یادآوری پیامکی — ۳ روز مانده به حرکت قطار',
  train_reminder_1d: 'یادآوری پیامکی — ۱ روز مانده به حرکت قطار',
  train_reminder_30m: 'یادآوری پیامکی — ۳۰ دقیقه مانده به حرکت قطار',

  kings_cross_coming_soon_title: 'عنوان صفحه‌ی کینگزکراس (به‌زودی)',
  kings_cross_coming_soon_body: 'متن صفحه‌ی کینگزکراس (به‌زودی)',

  ministry_post_notice_fa: 'اطلاعیه‌ی وزارت سحر و جادو (توقف پست جغدی)',
  under_11_wait_notice: 'پیام «صبر کن تا ۱۱ ساله شوی» (زیر سن)',
  apology_letter_fa: 'نامه‌ی عذرخواهی (فارسی) — برای گیرندگان بالای ۱۱ سال',
  apology_letter_en: 'نامه‌ی عذرخواهی (انگلیسی) — برای گیرندگان بالای ۱۱ سال'
};

// ── گروه‌بندی فقط برای نمایش مرتب‌تر در پنل — روی داده‌ی ذخیره‌شده تأثیری ندارد
const GROUPS = [
  {
    title: 'ثبت‌نام و دعوت‌نامه',
    keys: ['registration_closed', 'registration_closed_waitlist', 'invite_required', 'invite_invalid', 'invite_expired', 'invite_used', 'invite_wrong_phone', 'waitlist_disabled']
  },
  {
    title: 'سکوی ۹ و ۳/۴',
    keys: ['platform_pass', 'platform_pass_with_ticket', 'platform_fail_waitlist_auto_invite', 'platform_fail_public_registration', 'platform_fail_friend_referral', 'platform_fail_generic', 'platform_attempts_exhausted', 'initial_galleons_message']
  },
  {
    title: 'پیامک‌ها (SMS)',
    keys: ['waitlist_confirmation_sms', 'platform_success_sms', 'invitation_gift_sms']
  },
  {
    title: 'قطار هاگوارتز اکسپرس',
    keys: ['train_missed_notice', 'train_reminder_3d', 'train_reminder_1d', 'train_reminder_30m']
  },
  {
    title: 'ایستگاه کینگزکراس',
    keys: ['kings_cross_coming_soon_title', 'kings_cross_coming_soon_body']
  },
  {
    title: 'نامه‌ی دیرهنگام / عذرخواهی (بالای ۱۱ سال)',
    keys: ['ministry_post_notice_fa', 'under_11_wait_notice', 'apology_letter_fa', 'apology_letter_en'],
    highlight: true
  }
];

export default function SiteMessagesManager() {
  const [original, setOriginal] = useState({});
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getAppConfig('site_messages');
      const v = res.data?.config?.value || {};
      setOriginal(v);
      setValues(v);
    } catch (error) {
      message.error('خطا در بارگذاری پیام‌های سایت');
    } finally {
      setLoading(false);
    }
  };

  // نکته‌ی مهم: PUT روی app-config کل مقدار را جایگزین می‌کند (نه merge) —
  // پس همیشه باید کل آبجکت values (نه فقط فیلد تغییریافته) ارسال شود.
  const saveAll = async () => {
    setSaving(true);
    try {
      const res = await adminAPI.upsertAppConfig('site_messages', values);
      if (res.data?.success) {
        setOriginal(values);
        message.success('همه‌ی پیام‌ها ذخیره شد');
      }
    } catch (error) {
      message.error('خطا در ذخیره‌سازی');
    } finally {
      setSaving(false);
    }
  };

  const resetAllToDefault = async () => {
    setSaving(true);
    try {
      const res = await adminAPI.resetAppConfig('site_messages');
      if (res?.data?.success) {
        const v = res.data.config?.value || {};
        setOriginal(v);
        setValues(v);
        message.success('همه‌ی پیام‌ها به مقدار پیش‌فرض بازگردانده شد');
      }
    } catch (error) {
      message.error('خطا در بازگردانی به پیش‌فرض');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(original) !== JSON.stringify(values);

  const allKnownKeys = GROUPS.flatMap((g) => g.keys);
  const extraKeys = Object.keys(values).filter((k) => !allKnownKeys.includes(k));

  if (loading) return <Spin style={{ display: 'block', margin: '48px auto' }} />;

  const renderField = (key) => (
    <div key={key} style={{ marginBottom: 18 }}>
      <div style={{ fontWeight: 600, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
        {LABELS[key] || key}
        <Tag style={{ fontFamily: 'monospace' }}>{key}</Tag>
        {values[key] !== original[key] && <Tag color="gold">تغییر یافته</Tag>}
      </div>
      <TextArea
        value={values[key] ?? ''}
        onChange={(e) => setValues({ ...values, [key]: e.target.value })}
        autoSize={{ minRows: 2, maxRows: 8 }}
        dir={/[A-Za-z]/.test(key) && !/fa$/.test(key) ? 'auto' : 'rtl'}
      />
    </div>
  );

  return (
    <div style={{ maxWidth: 900, display: 'flex', flexDirection: 'column', gap: 20 }}>
      <Card>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="پیام‌های نمایشی سایت"
          description="تمام متن‌های ثابتی که در جریان بازی (پیامک‌ها، پیام‌های سکو، نامه‌ی عذرخواهی و...) به کاربر نمایش داده می‌شود از همین‌جا قابل ویرایش است — بدون نیاز به دیپلوی مجدد."
        />
        <Space>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={!hasChanges} onClick={saveAll}>
            ذخیره‌ی همه‌ی تغییرات
          </Button>
          <Button icon={<ReloadOutlined />} onClick={load} disabled={saving}>
            بازخوانی (لغو تغییرات ذخیره‌نشده)
          </Button>
          <Popconfirm
            title="بازگردانی همه‌ی پیام‌های سایت به مقدار پیش‌فرض؟"
            description="هر ویرایش دستی روی هر کدام از پیام‌ها از بین می‌رود."
            okText="بله، بازگردان"
            cancelText="انصراف"
            onConfirm={resetAllToDefault}
          >
            <Button danger icon={<UndoOutlined />} disabled={saving}>
              بازگردانی همه به پیش‌فرض
            </Button>
          </Popconfirm>
        </Space>
      </Card>

      <Collapse
        defaultActiveKey={GROUPS.filter((g) => g.highlight).map((g) => g.title)}
        items={GROUPS.map((group) => ({
          key: group.title,
          label: group.highlight ? <strong style={{ color: '#7a1f1f' }}>{group.title}</strong> : group.title,
          children: group.keys.filter((k) => k in values).map(renderField)
        }))}
      />

      {extraKeys.length > 0 && (
        <Card title="سایر کلیدها (بدون برچسب از پیش تعریف‌شده)">
          {extraKeys.map(renderField)}
        </Card>
      )}
    </div>
  );
}
